import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Brain,
  CalendarClock,
  ChevronDown,
  ListChecks,
  Loader2,
  ScrollText,
  Trash2,
  UserRound,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useMemoryInsights } from '../hooks/useMemoryInsights';
import {
  createSuppressionRule,
  deleteMemoryCommitment,
  deleteMemoryEpisode,
  deleteMemoryFact,
  deleteMemoryPlaybookEntry,
  setSuppressionRuleStatus,
} from '../services/memory';
import { getSession } from '../services/sessions';
import { getConversationSessionDate, getConversationSessionSummary, getConversationSessionTitle } from '../components/session/ConversationSessionCard';
import type { FlankTimestamp, MemoryItemKind, Session } from '../types';

type MemorySectionKey = 'about' | 'commitments' | 'events' | 'coaching';
type MemoryRange = 'today' | 'all';

type MemoryRow = {
  id: string;
  text: string;
  meta?: string;
  detailText?: string;
  mentionCount: number;
  sourceSessionIds: string[];
  itemKind: Exclude<MemoryItemKind, 'belief'>;
};

interface PendingRemoval {
  kind: Exclude<MemoryItemKind, 'belief'>;
  id: string;
  label: string;
}

export default function MemoriesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { facts, episodes, playbook, commitments, suppressionRules, loading } = useMemoryInsights(user?.uid ?? null);
  const [expanded, setExpanded] = useState<Record<MemorySectionKey, boolean>>({
    about: false,
    commitments: false,
    events: false,
    coaching: false,
  });
  const [showSuppressionManager, setShowSuppressionManager] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval | null>(null);
  const [removing, setRemoving] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const selectedRange: MemoryRange = searchParams.get('range') === 'today' ? 'today' : 'all';
  const todayRange = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return { start, end };
  }, []);

  const setRange = (range: MemoryRange) => {
    const next = new URLSearchParams(searchParams);
    next.set('range', range);
    setSearchParams(next, { replace: true });
  };

  const filteredFacts = useMemo(
    () => facts.filter((item) => selectedRange === 'all' || isTimestampInRange(item.createdAt, todayRange.start, todayRange.end)),
    [facts, selectedRange, todayRange.end, todayRange.start]
  );
  const filteredEpisodes = useMemo(
    () => episodes.filter((item) => selectedRange === 'all' || isTimestampInRange(item.createdAt, todayRange.start, todayRange.end)),
    [episodes, selectedRange, todayRange.end, todayRange.start]
  );
  const filteredPlaybook = useMemo(
    () => playbook.filter((item) => selectedRange === 'all' || isTimestampInRange(item.createdAt, todayRange.start, todayRange.end)),
    [playbook, selectedRange, todayRange.end, todayRange.start]
  );
  const filteredCommitments = useMemo(
    () => commitments.filter((item) => selectedRange === 'all' || isTimestampInRange(item.createdAt, todayRange.start, todayRange.end)),
    [commitments, selectedRange, todayRange.end, todayRange.start]
  );

  const openCommitments = useMemo(
    () => filteredCommitments.filter((item) => item.status === 'open'),
    [filteredCommitments]
  );
  const hasAnyMemories = filteredFacts.length > 0
    || openCommitments.length > 0
    || filteredEpisodes.length > 0
    || filteredPlaybook.length > 0;
  const activeSuppressionCount = suppressionRules.filter((rule) => rule.status === 'active').length;

  const aboutRows: MemoryRow[] = sortMemoryRowsByMentionsDesc(filteredFacts.map((fact) => ({
    id: fact.id,
    text: fact.content,
    meta: fact.category,
    mentionCount: getMemoryMentionCount(fact.mentionCount, fact.sourceSessionIds, fact.sourceSessionId),
    sourceSessionIds: getMemorySourceSessionIds(fact.sourceSessionIds, fact.sourceSessionId),
    itemKind: 'memory_fact',
  })));
  const commitmentRows: MemoryRow[] = sortMemoryRowsByMentionsDesc(openCommitments.map((item) => ({
    id: item.id,
    text: item.description,
    meta: item.followUpPriority.replace(/_/g, ' '),
    mentionCount: getMemoryMentionCount(item.mentionCount, item.sourceSessionIds, item.sourceSessionId),
    sourceSessionIds: getMemorySourceSessionIds(item.sourceSessionIds, item.sourceSessionId),
    itemKind: 'memory_commitment',
  })));
  const eventRows: MemoryRow[] = sortMemoryRowsByMentionsDesc(filteredEpisodes.map((episode) => ({
    id: episode.id,
    text: episode.summary,
    meta: episode.emotionalSignificance,
    mentionCount: getMemoryMentionCount(episode.mentionCount, episode.sourceSessionIds, episode.sourceSessionId),
    sourceSessionIds: getMemorySourceSessionIds(episode.sourceSessionIds, episode.sourceSessionId),
    itemKind: 'memory_episode',
  })));
  const coachingRows: MemoryRow[] = sortMemoryRowsByMentionsDesc(filteredPlaybook.map((note) => ({
    id: note.id,
    text: note.observation,
    meta: note.confidence ? `${Math.round(note.confidence * 100)}% confidence` : undefined,
    detailText: note.evidence?.trim() || undefined,
    mentionCount: getMemoryMentionCount(note.mentionCount, note.sourceSessionIds, note.sourceSessionId),
    sourceSessionIds: getMemorySourceSessionIds(note.sourceSessionIds, note.sourceSessionId),
    itemKind: 'memory_playbook',
  })));

  async function handleDelete(withSuppression: boolean) {
    if (!user || !pendingRemoval) return;
    setRemoving(true);
    setFeedback(null);
    setError(null);
    try {
      if (pendingRemoval.kind === 'memory_fact') {
        await deleteMemoryFact(user.uid, pendingRemoval.id);
      } else if (pendingRemoval.kind === 'memory_episode') {
        await deleteMemoryEpisode(user.uid, pendingRemoval.id);
      } else if (pendingRemoval.kind === 'memory_playbook') {
        await deleteMemoryPlaybookEntry(user.uid, pendingRemoval.id);
      } else {
        await deleteMemoryCommitment(user.uid, pendingRemoval.id);
      }

      if (withSuppression) {
        await createSuppressionRule(user.uid, pendingRemoval.label, pendingRemoval.kind);
      }

      setFeedback(
        withSuppression
          ? `Deleted "${pendingRemoval.label}" and added to suppressed concepts.`
          : `Deleted "${pendingRemoval.label}".`
      );
      setPendingRemoval(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete memory');
    } finally {
      setRemoving(false);
    }
  }

  async function toggleSuppression(ruleId: string, currentStatus: 'active' | 'disabled') {
    if (!user) return;
    try {
      await setSuppressionRuleStatus(user.uid, ruleId, currentStatus === 'active' ? 'disabled' : 'active');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update suppression rule');
    }
  }

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <header className="sticky top-0 z-20 bg-[var(--color-background)] border-b border-[var(--color-border)] px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate('/settings?tab=insights')}
          className="p-2 -ml-2 rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)] transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="font-semibold text-[var(--color-text-primary)]">Memories</h1>
        <button
          onClick={() => navigate(`/insights?range=${selectedRange}&sort=mentions`)}
          className="ml-auto text-xs px-2 py-1 rounded-md border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface)]"
        >
          Beliefs
        </button>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 flex flex-col gap-4">
        <div className="flank-card rounded-xl p-3">
          <div className="inline-flex rounded-lg border border-[var(--color-border)] p-0.5">
            <button
              onClick={() => setRange('today')}
              className="px-2.5 py-1 rounded-md text-xs font-medium transition-colors"
              style={{
                background: selectedRange === 'today' ? 'var(--color-surface-elevated)' : 'transparent',
                color: selectedRange === 'today' ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              }}
            >
              Today
            </button>
            <button
              onClick={() => setRange('all')}
              className="px-2.5 py-1 rounded-md text-xs font-medium transition-colors"
              style={{
                background: selectedRange === 'all' ? 'var(--color-surface-elevated)' : 'transparent',
                color: selectedRange === 'all' ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
              }}
            >
              All memories
            </button>
          </div>
        </div>

        {(feedback || error) && (
          <p className="text-xs" style={{ color: error ? 'var(--color-error)' : 'var(--color-success)' }}>
            {error ?? feedback}
          </p>
        )}

        {loading ? (
          <div className="flank-card rounded-xl p-5 flex items-center justify-center">
            <Loader2 size={18} className="animate-spin text-[var(--color-text-secondary)]" />
          </div>
        ) : (
          <>
            <section className="flank-card rounded-xl p-4">
              {!hasAnyMemories && (
                <div className="mb-3 px-3 py-2 rounded-lg bg-[var(--color-surface)]">
                  <p className="text-sm font-medium text-[var(--color-text-primary)]">No memories yet</p>
                  <p className="text-xs text-[var(--color-text-secondary)] mt-1">
                    After conversations are analyzed, memory items will appear in these sections.
                  </p>
                </div>
              )}

              <MemoryAccordion
                userId={user?.uid ?? null}
                icon={UserRound}
                title="About You"
                count={aboutRows.length}
                expanded={expanded.about}
                onToggle={() => setExpanded((prev) => ({ ...prev, about: !prev.about }))}
                rows={aboutRows}
                emptyLabel="No personal facts captured yet."
                onRemove={(row) => setPendingRemoval({ kind: row.itemKind, id: row.id, label: row.text })}
              />
              <MemoryAccordion
                userId={user?.uid ?? null}
                icon={ListChecks}
                title="Open Commitments"
                count={commitmentRows.length}
                expanded={expanded.commitments}
                onToggle={() => setExpanded((prev) => ({ ...prev, commitments: !prev.commitments }))}
                rows={commitmentRows}
                emptyLabel="No active commitments."
                onRemove={(row) => setPendingRemoval({ kind: row.itemKind, id: row.id, label: row.text })}
              />
              <MemoryAccordion
                userId={user?.uid ?? null}
                icon={CalendarClock}
                title="Significant Events"
                count={eventRows.length}
                expanded={expanded.events}
                onToggle={() => setExpanded((prev) => ({ ...prev, events: !prev.events }))}
                rows={eventRows}
                emptyLabel="No episodes captured yet."
                onRemove={(row) => setPendingRemoval({ kind: row.itemKind, id: row.id, label: row.text })}
              />
              <MemoryAccordion
                userId={user?.uid ?? null}
                icon={Brain}
                title="Coaching Notes"
                count={coachingRows.length}
                expanded={expanded.coaching}
                onToggle={() => setExpanded((prev) => ({ ...prev, coaching: !prev.coaching }))}
                rows={coachingRows}
                emptyLabel="No playbook notes captured yet."
                onRemove={(row) => setPendingRemoval({ kind: row.itemKind, id: row.id, label: row.text })}
              />
            </section>

            <section className="rounded-xl border border-[var(--color-border)]/70 bg-[var(--color-surface)]/30 p-3">
              <button
                onClick={() => setShowSuppressionManager((prev) => !prev)}
                className="w-full min-h-[36px] px-1 rounded-lg flex items-center gap-2 text-left text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-surface)] transition-colors"
              >
                <ScrollText size={13} className="text-[var(--color-text-muted)]" />
                <span>Manage suppressed concepts</span>
                <span className="text-[11px]">{activeSuppressionCount}</span>
                <ChevronDown
                  size={13}
                  className="ml-auto text-[var(--color-text-muted)] transition-transform duration-200"
                  style={{ transform: showSuppressionManager ? 'rotate(180deg)' : undefined }}
                />
              </button>
              {showSuppressionManager && (
                <div className="mt-1">
                  {suppressionRules.length === 0 && (
                    <p className="text-xs text-[var(--color-text-muted)] px-3 py-2">No suppression rules yet.</p>
                  )}
                  {suppressionRules.map((rule) => (
                    <div key={rule.id} className="px-3 py-2.5 flex items-center gap-2 border-b border-[var(--color-border)] last:border-b-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[var(--color-text-secondary)] truncate">{rule.conceptText}</p>
                        <p className="text-[11px] text-[var(--color-text-muted)] mt-0.5">
                          {rule.itemKind.replace('memory_', '').replace('_', ' ')} · {rule.status}
                        </p>
                      </div>
                      <button
                        onClick={() => toggleSuppression(rule.id, rule.status)}
                        className="px-2 py-1 rounded-md text-[11px] border border-[var(--color-border)] text-[var(--color-text-secondary)]"
                      >
                        {rule.status === 'active' ? 'Re-enable' : 'Enable'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>

      {pendingRemoval && (
        <div className="fixed inset-0 z-50 bg-black/40 p-4 flex items-end sm:items-center justify-center">
          <div className="w-full max-w-sm rounded-2xl p-5 bg-[var(--color-background)] border border-[var(--color-border)]">
            <h3 className="font-semibold text-[var(--color-text-primary)]">Delete memory?</h3>
            <p className="text-sm text-[var(--color-text-secondary)] mt-1">
              Remove "{pendingRemoval.label}" from your memories?
            </p>
            <p className="text-xs text-[var(--color-text-muted)] mt-2">
              Use "Delete and don&apos;t relearn" to suppress semantically similar future extractions.
            </p>

            <button
              onClick={() => handleDelete(true)}
              disabled={removing}
              className="w-full mt-4 h-9 rounded-lg border text-sm"
              style={{
                borderColor: 'color-mix(in srgb, var(--color-error) 50%, var(--color-border))',
                color: 'var(--color-error)',
              }}
            >
              {removing ? 'Working...' : "Delete and don't relearn"}
            </button>

            <div className="mt-3 grid grid-cols-2 gap-2">
              <button
                onClick={() => setPendingRemoval(null)}
                disabled={removing}
                className="h-9 rounded-lg border border-[var(--color-border)] text-sm text-[var(--color-text-secondary)]"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(false)}
                disabled={removing}
                className="h-9 rounded-lg text-sm text-white"
                style={{ background: 'var(--color-error)' }}
              >
                {removing ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MemoryAccordion({
  userId,
  icon: Icon,
  title,
  count,
  expanded,
  onToggle,
  rows,
  emptyLabel,
  onRemove,
}: {
  userId: string | null;
  icon: typeof UserRound;
  title: string;
  count: number;
  expanded: boolean;
  onToggle: () => void;
  rows: MemoryRow[];
  emptyLabel: string;
  onRemove: (row: MemoryRow) => void;
}) {
  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [expandedSessionRows, setExpandedSessionRows] = useState<Record<string, boolean>>({});
  const [sessionMetaById, setSessionMetaById] = useState<Record<string, LinkedSessionMeta>>({});
  const [loadingSessionRows, setLoadingSessionRows] = useState<Record<string, boolean>>({});
  const [sessionErrorsByRow, setSessionErrorsByRow] = useState<Record<string, string>>({});

  async function loadSessionMetaForRow(row: MemoryRow) {
    if (!userId || row.sourceSessionIds.length === 0) return;
    const missingSessionIds = row.sourceSessionIds.filter((sessionId) => !sessionMetaById[sessionId]);
    if (missingSessionIds.length === 0) return;

    setLoadingSessionRows((prev) => ({ ...prev, [row.id]: true }));
    setSessionErrorsByRow((prev) => ({ ...prev, [row.id]: '' }));

    try {
      const sessions = await Promise.all(
        missingSessionIds.map(async (sessionId) => ({
          sessionId,
          session: await getSession(userId, sessionId),
        }))
      );

      setSessionMetaById((prev) => {
        const next = { ...prev };
        for (const { sessionId, session } of sessions) {
          if (!session) continue;
          next[sessionId] = buildLinkedSessionMeta(session);
        }
        return next;
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not load sessions';
      setSessionErrorsByRow((prev) => ({ ...prev, [row.id]: message }));
    } finally {
      setLoadingSessionRows((prev) => ({ ...prev, [row.id]: false }));
    }
  }

  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full min-h-[42px] px-2 rounded-lg flex items-center gap-2 text-left hover:bg-[var(--color-surface)] transition-colors"
      >
        <Icon size={14} className="text-[var(--color-text-muted)]" />
        <p className="text-[13px] uppercase tracking-wider text-[var(--color-text-secondary)]">{title}</p>
        <span className="text-xs text-[var(--color-text-muted)]">{count}</span>
        <ChevronDown
          size={14}
          className="ml-auto text-[var(--color-text-muted)] transition-transform duration-200"
          style={{ transform: expanded ? 'rotate(180deg)' : undefined }}
        />
      </button>
      {expanded && (
        <div className="mt-1">
          {rows.length === 0 && (
            <p className="text-xs text-[var(--color-text-muted)] px-3 py-2">{emptyLabel}</p>
          )}
          <div className="flex flex-col gap-2.5">
            {rows.map((row) => {
              const isRowExpanded = Boolean(expandedRows[row.id]);
              const isSessionsExpanded = Boolean(expandedSessionRows[row.id]);
              return (
                <div
                  key={row.id}
                  className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedRows((prev) => ({ ...prev, [row.id]: !prev[row.id] }))}
                    className="w-full px-3 py-3 flex items-start gap-2 text-left hover:bg-[var(--color-surface-elevated)] transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[var(--color-text-primary)] truncate">{row.text}</p>
                      {row.meta && (
                        <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                          {row.meta}
                        </p>
                      )}
                      <p className="text-[11px] text-[var(--color-text-secondary)] mt-1">
                        {row.mentionCount} {row.mentionCount === 1 ? 'mention' : 'mentions'} · {row.sourceSessionIds.length} {row.sourceSessionIds.length === 1 ? 'session' : 'sessions'}
                      </p>
                    </div>
                    <ChevronDown
                      size={16}
                      className="mt-0.5 text-[var(--color-text-muted)] transition-transform duration-200"
                      style={{ transform: isRowExpanded ? 'rotate(180deg)' : undefined }}
                    />
                  </button>

                  {isRowExpanded && (
                    <div className="px-3 pb-3 border-t border-[var(--color-border)]">
                      <div className="pt-2 flex justify-end">
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            onRemove(row);
                          }}
                          className="p-1.5 rounded-md text-[var(--color-text-muted)] hover:text-[var(--color-error)] hover:bg-[var(--color-surface-elevated)]"
                          aria-label={`Delete ${row.text}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>

                      {row.detailText && (
                        <p className="text-xs text-[var(--color-text-secondary)]">
                          {row.detailText}
                        </p>
                      )}

                      <div className="mt-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-background)]">
                        <button
                          onClick={(event) => {
                            event.stopPropagation();
                            const nextExpanded = !isSessionsExpanded;
                            setExpandedSessionRows((prev) => ({ ...prev, [row.id]: nextExpanded }));
                            if (nextExpanded) {
                              void loadSessionMetaForRow(row);
                            }
                          }}
                          className="w-full px-3 py-2 flex items-center gap-2 text-left hover:bg-[var(--color-surface)] transition-colors"
                        >
                          <p className="text-xs font-medium text-[var(--color-text-secondary)]">
                            Sessions ({row.sourceSessionIds.length})
                          </p>
                          <ChevronDown
                            size={14}
                            className="ml-auto text-[var(--color-text-muted)] transition-transform duration-200"
                            style={{ transform: isSessionsExpanded ? 'rotate(180deg)' : undefined }}
                          />
                        </button>

                        {isSessionsExpanded && (
                          <div className="px-3 pb-3 border-t border-[var(--color-border)]">
                            {row.sourceSessionIds.length === 0 ? (
                              <p className="pt-2 text-[11px] text-[var(--color-text-muted)]">
                                No linked sessions yet.
                              </p>
                            ) : (
                              <div className="pt-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-elevated)] p-2 flex flex-col gap-2">
                                {getSortedLinkedSessionsForMemoryRow(row, sessionMetaById).map((linkedSession) => (
                                  <button
                                    key={linkedSession.id}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      navigate(`/conversations/${linkedSession.id}`);
                                    }}
                                    className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-2.5 py-2 text-left hover:bg-[var(--color-surface)] transition-colors"
                                  >
                                    <p className="text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                                      {formatLinkedSessionDate(linkedSession.startedAt)}
                                    </p>
                                    <p className="mt-0.5 text-xs font-medium text-[var(--color-text-primary)] line-clamp-1">
                                      {linkedSession.title}
                                    </p>
                                  </button>
                                ))}
                                {loadingSessionRows[row.id] && (
                                  <p className="text-[11px] text-[var(--color-text-muted)]">
                                    Loading session details...
                                  </p>
                                )}
                                {sessionErrorsByRow[row.id] && !loadingSessionRows[row.id] && (
                                  <p className="text-[11px] text-[var(--color-error)]">
                                    {sessionErrorsByRow[row.id]}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function getMemoryMentionCount(
  mentionCount?: number,
  sourceSessionIds?: string[],
  sourceSessionId?: string
): number {
  if (typeof mentionCount === 'number' && mentionCount > 0) return mentionCount;
  if (sourceSessionIds?.length) return sourceSessionIds.length;
  if (sourceSessionId) return 1;
  return 1;
}

function getMemorySourceSessionIds(sourceSessionIds?: string[], sourceSessionId?: string): string[] {
  const sessions = sourceSessionIds?.filter(Boolean) ?? [];
  if (sessions.length > 0) return Array.from(new Set(sessions));
  return sourceSessionId ? [sourceSessionId] : [];
}

function sortMemoryRowsByMentionsDesc(rows: MemoryRow[]): MemoryRow[] {
  return [...rows].sort((a, b) => {
    const mentionDiff = b.mentionCount - a.mentionCount;
    if (mentionDiff !== 0) return mentionDiff;

    const sourceDiff = b.sourceSessionIds.length - a.sourceSessionIds.length;
    if (sourceDiff !== 0) return sourceDiff;

    return a.text.localeCompare(b.text);
  });
}

interface LinkedSessionMeta {
  id: string;
  startedAt: Date;
  title: string;
}

function buildLinkedSessionMeta(session: Session): LinkedSessionMeta {
  const explicitTitle = session.outputs?.sessionTitle?.trim()
    || session.outputs?.taskTitle?.trim();
  const summaryFallback = getConversationSessionSummary(session)?.trim();

  return {
    id: session.id,
    startedAt: getConversationSessionDate(session),
    title: explicitTitle || summaryFallback || getConversationSessionTitle(session),
  };
}

function getSortedLinkedSessionsForMemoryRow(
  row: MemoryRow,
  sessionMetaById: Record<string, LinkedSessionMeta>
): LinkedSessionMeta[] {
  const fromMeta = row.sourceSessionIds
    .map((sessionId) => sessionMetaById[sessionId] ?? null)
    .filter((session): session is LinkedSessionMeta => Boolean(session));

  const missing = row.sourceSessionIds
    .filter((sessionId) => !sessionMetaById[sessionId])
    .map((sessionId) => buildFallbackSessionMeta(row, sessionId));

  return [...fromMeta, ...missing].sort((a, b) => a.startedAt.getTime() - b.startedAt.getTime());
}

function buildFallbackSessionMeta(row: MemoryRow, sessionId: string): LinkedSessionMeta {
  const fallbackTitle = row.text.trim() || 'Conversation';
  return {
    id: sessionId,
    startedAt: new Date(0),
    title: fallbackTitle,
  };
}

function formatLinkedSessionDate(date: Date): string {
  if (Number.isNaN(date.getTime()) || date.getTime() <= 0) return 'Date unavailable';
  const dateLabel = date.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
  const timeLabel = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${dateLabel} · ${timeLabel}`;
}

function isTimestampInRange(value: FlankTimestamp, start: Date, end: Date): boolean {
  const date = value?.toDate ? value.toDate() : new Date(value.seconds * 1000);
  return date >= start && date < end;
}
